import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { money } from "@/lib/format";

import {
  PageHeader,
  Loader,
  EmptyState,
  KpiCard,
} from "@/components/common/Bits";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

import {
  IndianRupee,
  ShoppingBag,
  Receipt,
  Percent,
  Download,
  BarChart3,
  FileBarChart,
  Wallet,
  TrendingUp,
  TrendingDown,
  CalendarDays,
} from "lucide-react";

import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

import { toast } from "sonner";

const COLORS = [
  "#EA580C",
  "#16A34A",
  "#0284C7",
  "#F59E0B",
  "#8B5CF6",
  "#DC2626",
];

function formatDateInput(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
}

function getPresetRange(type) {
  const now = new Date();

  if (type === "today") {
    const value = formatDateInput(now);
    return {
      date_from: value,
      date_to: value,
    };
  }

  if (type === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      date_from: formatDateInput(start),
      date_to: formatDateInput(now),
    };
  }

  return {
    date_from: "",
    date_to: "",
  };
}

export default function Reports() {
  const { branchId } = useApp();

  // Filter values currently selected in the UI.
  // These do NOT trigger an API request until Apply is clicked.
  const [preset, setPreset] = useState("month");

  const initialMonth = getPresetRange("month");

  const [dateFrom, setDateFrom] = useState(initialMonth.date_from);
  const [dateTo, setDateTo] = useState(initialMonth.date_to);

  // Dates actually applied to the report API.
  const [appliedDateFrom, setAppliedDateFrom] = useState(
    initialMonth.date_from,
  );
  const [appliedDateTo, setAppliedDateTo] = useState(initialMonth.date_to);

  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [itemSalesPage, setItemSalesPage] = useState(1);
  const [itemSalesPageSize, setItemSalesPageSize] = useState(10);

  const loadReport = useCallback(() => {
    setLoading(true);

    api
      .reports(branchId, {
        date_from: appliedDateFrom || undefined,
        date_to: appliedDateTo || undefined,
      })
      .then((r) => {
        setD(r.data);
      })
      .catch(() => {
        setD(null);
        toast.error("Unable to load report");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [branchId, appliedDateFrom, appliedDateTo]);

  // Load on first render and whenever branch/applied filter changes.
  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const applyPreset = (type) => {
    setPreset(type);

    const range = getPresetRange(type);

    setDateFrom(range.date_from);
    setDateTo(range.date_to);

    // Presets are immediately applied.
    setAppliedDateFrom(range.date_from);
    setAppliedDateTo(range.date_to);
  };

  const applyCustomDate = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Please select both dates");
      return;
    }

    if (dateFrom > dateTo) {
      toast.error("From date cannot be after To date");
      return;
    }

    setPreset("custom");
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
  };

  const clearDateRange = () => {
    setDateFrom("");
    setDateTo("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
    setPreset("all");
  };

  const hasSalesData = useMemo(() => {
    if (!d) return false;

    return (
      Number(d.total_sales || 0) > 0 ||
      Number(d.total_orders || 0) > 0 ||
      Number(d.total_expenses || 0) > 0 ||
      (Array.isArray(d.item_sales) && d.item_sales.length > 0) ||
      (Array.isArray(d.category_sales) && d.category_sales.length > 0)
    );
  }, [d]);

  const hasCategoryData =
    Array.isArray(d?.category_sales) && d.category_sales.length > 0;

  const hasPaymentData =
    Array.isArray(d?.payment_report) && d.payment_report.length > 0;

  const hasItemData = Array.isArray(d?.item_sales) && d.item_sales.length > 0;
  const itemSales = Array.isArray(d?.item_sales) ? d.item_sales : [];

  const itemSalesTotal = itemSales.length;

  const itemSalesTotalPages = Math.max(
    1,
    Math.ceil(itemSalesTotal / itemSalesPageSize),
  );

  const paginatedItemSales = itemSales.slice(
    (itemSalesPage - 1) * itemSalesPageSize,
    itemSalesPage * itemSalesPageSize,
  );

  const itemSalesStart =
    itemSalesTotal === 0 ? 0 : (itemSalesPage - 1) * itemSalesPageSize + 1;

  const itemSalesEnd = Math.min(
    itemSalesPage * itemSalesPageSize,
    itemSalesTotal,
  );

  const itemSalesTotalQty = itemSales.reduce(
    (sum, item) => sum + Number(item.qty || 0),
    0,
  );

  const itemSalesTotalRevenue = itemSales.reduce(
    (sum, item) => sum + Number(item.revenue || 0),
    0,
  );

  useEffect(() => {
    setItemSalesPage(1);
  }, [appliedDateFrom, appliedDateTo, branchId]);
  const hasOrderTypeData =
    Array.isArray(d?.order_type_sales) && d.order_type_sales.length > 0;

  const exportCSV = () => {
    if (!d) return;

    const rows = [
      ["STAYSERVE BUSINESS REPORT"],
      [],
      ["Date From", d.date_from || "All"],
      ["Date To", d.date_to || "All"],
      [],
      ["BUSINESS SUMMARY"],
      ["Gross Sales", d.total_sales || 0],
      ["Discounts", d.total_discount || 0],
      ["Sales Before GST", d.sales_before_tax || 0],
      ["GST Collected", d.total_tax || 0],
      ["Orders", d.total_orders || 0],
      ["Average Order Value", d.avg_order_value || 0],
      ["Operating Expenses", d.total_expenses || 0],
      ["Operating Profit / Loss", d.operating_profit || 0],
      [],
      ["ITEM SALES"],
      ["Item", "Qty", "Revenue"],
      ...(d.item_sales || []).map((i) => [i.name, i.qty, i.revenue]),
    ];

    const csv = rows
      .map((row) =>
        row
          .map((value) => {
            const text = String(value ?? "");
            return `"${text.replace(/"/g, '""')}"`;
          })
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "stayserve-business-report.csv";

    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);

    toast.success("Report exported");
  };

  if (loading) {
    return (
      <div className="p-6">
        <Loader label="Building reports" />
      </div>
    );
  }

  if (!d) {
    return (
      <div className="p-6">
        <EmptyState
          icon={FileBarChart}
          title="Unable to load report"
          subtitle="Please try again."
        />
      </div>
    );
  }

  const profit = Number(d.operating_profit || 0);
  const profitPositive = profit >= 0;

  return (
    <div className="p-4 sm:p-6 animate-fade-up">
      <PageHeader
        title="Reports & Analytics"
        subtitle="Complete business performance report"
        actions={
          <Button
            variant="outline"
            onClick={exportCSV}
            className="rounded-xl font-semibold"
            data-testid="report-export"
          >
            <Download size={16} className="mr-1.5" />
            Export CSV
          </Button>
        }
      />

      {/* ------------------------------------------------ */}
      {/* DATE FILTERS */}
      {/* ------------------------------------------------ */}

      <div className="rounded-2xl border border-border bg-card p-4 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays size={17} />
          <h3 className="font-display font-semibold">Report Period</h3>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          {/* RIGHT: Custom date range */}
          <div className="flex flex-wrap items-end gap-3 lg:justify-end">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">
                From
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPreset("custom");
                }}
                className="h-10 w-[150px] rounded-xl border border-border bg-white px-3 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">
                To
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPreset("custom");
                }}
                className="h-10 w-[150px] rounded-xl border border-border bg-white px-3 text-sm"
              />
            </div>

            <Button onClick={applyCustomDate} className="h-10 rounded-xl">
              Apply
            </Button>

            <Button
              variant="outline"
              onClick={clearDateRange}
              className="h-10 rounded-xl"
            >
              Clear
            </Button>
          </div>
          {/* LEFT: Preset filters */}
          <div className="flex flex-wrap items-center gap-2 lg:pt-5">
            {[
              ["all", "All"],
              ["month", "This Month"],
              ["today", "Today"],
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => applyPreset(value)}
                className={cn(
                  "rounded-full px-4 py-1.5 text-sm font-semibold transition-colors",
                  preset === value
                    ? "bg-primary text-white"
                    : "bg-white border border-border hover:bg-secondary",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-4">
          Showing report for <strong>{d.date_from || "all dates"}</strong> to{" "}
          <strong>{d.date_to || "all dates"}</strong>
        </p>
      </div>

      {/* ------------------------------------------------ */}
      {/* BUSINESS KPI */}
      {/* ------------------------------------------------ */}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiCard
          label="Gross Sales"
          value={money(d.total_sales || 0)}
          icon={IndianRupee}
          accent="orange"
        />

        <KpiCard
          label="Orders"
          value={d.total_orders || 0}
          icon={ShoppingBag}
          accent="sky"
        />

        <KpiCard
          label="Avg Order Value"
          value={money(d.avg_order_value || 0)}
          icon={Receipt}
          accent="emerald"
        />

        <KpiCard
          label="GST Collected"
          value={money(d.total_tax || 0)}
          icon={Percent}
          accent="violet"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label="Discounts"
          value={money(d.total_discount || 0)}
          icon={Percent}
          accent="orange"
        />

        <KpiCard
          label="Sales Before GST"
          value={money(d.sales_before_tax || 0)}
          icon={Receipt}
          accent="sky"
        />

        <KpiCard
          label="Operating Expenses"
          value={money(d.total_expenses || 0)}
          icon={Wallet}
          accent="violet"
        />

        <div
          className={cn(
            "rounded-2xl border p-5 bg-card",
            profitPositive ? "border-emerald-200" : "border-red-200",
          )}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Operating {profitPositive ? "Profit" : "Loss"}
            </span>

            {profitPositive ? (
              <TrendingUp size={20} className="text-emerald-600" />
            ) : (
              <TrendingDown size={20} className="text-red-600" />
            )}
          </div>

          <div
            className={cn(
              "text-2xl font-bold mt-4",
              profitPositive ? "text-emerald-600" : "text-red-600",
            )}
          >
            {money(Math.abs(profit))}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------ */}
      {/* NO DATA */}
      {/* ------------------------------------------------ */}

      {!hasSalesData ? (
        <div className="min-h-[430px] rounded-2xl border border-border bg-card flex items-center justify-center">
          <EmptyState
            icon={FileBarChart}
            title="No report data"
            subtitle="Sales, expenses and business activity will appear here once transactions are recorded."
          />
        </div>
      ) : (
        <>
          {/* ------------------------------------------------ */}
          {/* SALES ANALYTICS */}
          {/* ------------------------------------------------ */}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 rounded-2xl bg-card border border-border p-5">
              <h3 className="font-display font-semibold mb-4">
                Category Sales
              </h3>

              {hasCategoryData ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={d.category_sales} margin={{ left: -10 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#f0eeec"
                      vertical={false}
                    />

                    <XAxis
                      dataKey="name"
                      tick={{
                        fontSize: 11,
                        fill: "#78716C",
                      }}
                      axisLine={false}
                      tickLine={false}
                    />

                    <YAxis
                      tick={{
                        fontSize: 11,
                        fill: "#78716C",
                      }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) =>
                        `₹${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`
                      }
                    />

                    <Tooltip
                      formatter={(v) => money(v)}
                      contentStyle={{
                        borderRadius: 12,
                        fontSize: 13,
                      }}
                    />

                    <Bar
                      dataKey="revenue"
                      fill="#EA580C"
                      radius={[6, 6, 0, 0]}
                      barSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center">
                  <EmptyState
                    icon={BarChart3}
                    title="No category sales"
                    subtitle="Category performance will appear here."
                  />
                </div>
              )}
            </div>

            {/* PAYMENT METHODS */}

            <div className="rounded-2xl bg-card border border-border p-5">
              <h3 className="font-display font-semibold mb-4">
                Payment Methods
              </h3>

              {hasPaymentData ? (
                <>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={d.payment_report}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={3}
                      >
                        {d.payment_report.map((entry, index) => (
                          <Cell
                            key={entry.name || index}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>

                      <Tooltip
                        formatter={(v) => money(v)}
                        contentStyle={{
                          borderRadius: 12,
                          fontSize: 13,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>

                  <div className="flex flex-wrap gap-2 justify-center">
                    {d.payment_report.map((entry, index) => (
                      <span
                        key={entry.name}
                        className="flex items-center gap-1.5 text-xs"
                      >
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{
                            background: COLORS[index % COLORS.length],
                          }}
                        />

                        {entry.name}
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-[280px] flex items-center justify-center">
                  <EmptyState
                    icon={Receipt}
                    title="No payment data"
                    subtitle="Payment method breakdown will appear here."
                  />
                </div>
              )}
            </div>
          </div>

          {/* ------------------------------------------------ */}
          {/* ORDER TYPE */}
          {/* ------------------------------------------------ */}

          {hasOrderTypeData && (
            <div className="rounded-2xl bg-card border border-border p-5 mt-4">
              <h3 className="font-display font-semibold mb-4">
                Sales by Order Type
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {d.order_type_sales.map((item) => (
                  <div
                    key={item.name}
                    className="rounded-xl border border-border p-4"
                  >
                    <div className="text-sm text-muted-foreground">
                      {item.name}
                    </div>

                    <div className="text-xl font-bold mt-1">
                      {money(item.value)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ------------------------------------------------ */}
          {/* ITEM SALES */}
          {/* ------------------------------------------------ */}

          <div className="rounded-2xl bg-card border border-border overflow-hidden mt-4">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
              <div>
                <h3 className="font-display font-semibold">
                  Item Sales Report
                </h3>

                {hasItemData && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Showing {itemSalesStart}-{itemSalesEnd} of {itemSalesTotal}{" "}
                    items
                  </p>
                )}
              </div>

              {hasItemData && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Rows:</span>

                  <select
                    value={itemSalesPageSize}
                    onChange={(e) => {
                      setItemSalesPageSize(Number(e.target.value));
                      setItemSalesPage(1);
                    }}
                    className="h-9 rounded-lg border border-border bg-white px-2 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              )}
            </div>

            {hasItemData ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50 text-muted-foreground">
                      <tr className="text-center">
                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider w-16">
                          #
                        </th>

                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                          Item
                        </th>

                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                          Qty Sold
                        </th>

                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                          Revenue
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedItemSales.map((item, index) => {
                        const globalIndex =
                          (itemSalesPage - 1) * itemSalesPageSize + index;

                        return (
                          <tr
                            key={`${item.name}-${globalIndex}`}
                            className="border-t border-border hover:bg-secondary/30"
                          >
                            <td className="px-5 py-3 text-muted-foreground text-center">
                              {globalIndex + 1}
                            </td>

                            <td className="px-5 py-3 font-medium text-center">
                              {item.name || "Unknown"}
                            </td>

                            <td className="px-5 py-3 text-muted-foreground text-center">
                              {item.qty}
                            </td>

                            <td className="px-5 py-3 font-bold text-center">
                              {money(item.revenue)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>

                    <tfoot className="bg-secondary/30 border-t-2 border-border">
                      <tr>
                        <td className="px-5 py-3 text-center" />

                        <td className="px-5 py-3 font-semibold text-center">
                          Total
                        </td>

                        <td className="px-5 py-3 font-semibold text-center">
                          {itemSalesTotalQty}
                        </td>

                        <td className="px-5 py-3 font-bold text-center">
                          {money(itemSalesTotalRevenue)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* PAGINATION */}
                {itemSalesTotalPages > 1 && (
                  <div className="px-5 py-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="text-xs text-muted-foreground">
                      Showing{" "}
                      <span className="font-semibold text-foreground">
                        {itemSalesStart}
                      </span>
                      {" - "}
                      <span className="font-semibold text-foreground">
                        {itemSalesEnd}
                      </span>
                      {" of "}
                      <span className="font-semibold text-foreground">
                        {itemSalesTotal}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          setItemSalesPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={itemSalesPage === 1}
                        className="h-9 px-3 rounded-lg border border-border bg-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-secondary"
                      >
                        Previous
                      </button>

                      <div className="flex items-center gap-1 mx-1">
                        {Array.from(
                          { length: itemSalesTotalPages },
                          (_, index) => index + 1,
                        )
                          .filter((pageNumber) => {
                            if (itemSalesTotalPages <= 5) return true;

                            if (pageNumber === 1) return true;

                            if (pageNumber === itemSalesTotalPages) return true;

                            return Math.abs(pageNumber - itemSalesPage) <= 1;
                          })
                          .map((pageNumber, index, visiblePages) => {
                            const previousPage = visiblePages[index - 1];

                            const showEllipsis =
                              previousPage && pageNumber - previousPage > 1;

                            return (
                              <React.Fragment key={pageNumber}>
                                {showEllipsis && (
                                  <span className="px-1 text-muted-foreground">
                                    ...
                                  </span>
                                )}

                                <button
                                  type="button"
                                  onClick={() => setItemSalesPage(pageNumber)}
                                  className={cn(
                                    "h-9 min-w-9 px-2 rounded-lg text-sm font-medium transition-colors",
                                    itemSalesPage === pageNumber
                                      ? "bg-primary text-white"
                                      : "border border-border bg-white hover:bg-secondary",
                                  )}
                                >
                                  {pageNumber}
                                </button>
                              </React.Fragment>
                            );
                          })}
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setItemSalesPage((prev) =>
                            Math.min(itemSalesTotalPages, prev + 1),
                          )
                        }
                        disabled={itemSalesPage === itemSalesTotalPages}
                        className="h-9 px-3 rounded-lg border border-border bg-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-secondary"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="min-h-[260px] flex items-center justify-center">
                <EmptyState
                  icon={ShoppingBag}
                  title="No item sales yet"
                  subtitle="Item-wise sales will appear here once orders are completed."
                />
              </div>
            )}
          </div>

          {/* ------------------------------------------------ */}
          {/* ACCOUNTING NOTE */}
          {/* ------------------------------------------------ */}

          <div className="mt-4 rounded-xl border border-border bg-secondary/30 px-4 py-3 text-xs text-muted-foreground">
            <strong>Note:</strong> Operating Profit/Loss is calculated as Sales
            Before GST minus recorded operating expenses. Food cost / COGS is
            not included until inventory cost accounting is enabled.
          </div>
        </>
      )}
    </div>
  );
}
